import {
  getStartNode,
  getWorkflow,
  getNodes,
  getEdges,
  getWorkflowType,
} from 'modules/workflows/ducks';
import { WorkflowType } from 'modules/workflows/enums';
import { parseWorkflowForSaving } from 'modules/workflows/utils';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ApiMethod } from 'services/api';
import { useRestMutation } from 'utils/hooks/api';

const JOURNEY_VISITOR_ELIGIBILITY_URL = '/workflows/validateJvt';

type JourneyVisitorEligibility = {
  isJourneyEligible: boolean;
  jvtEligibilityMessage: string[];
};

export type GetJourneyVisitorEligibility = {
  checkCurrentJourneyEligibility: () => void;
  checkPublishedJourneyEligibility: () => void;
  isLoading: boolean;
  error: any;
  currentJourneyEligibility: JourneyVisitorEligibility;
  publishedJourneyEligibility: JourneyVisitorEligibility;
};

export const useGetJourneyVisitorEligibility = (): GetJourneyVisitorEligibility => {
  const { draftOnly, isPublished } = useSelector(getWorkflowType);
  const workflow = useSelector(getWorkflow);
  const nodes = useSelector(getNodes);
  const edges = useSelector(getEdges);

  // we need to have the nodes and edges in a state for the validation endpoint to parse properly
  const parsedWorkflowForSaving = parseWorkflowForSaving({ ...workflow, nodes, edges });
  const parsedNodes = parsedWorkflowForSaving?.nodes;
  const parsedEdges = parsedWorkflowForSaving?.edges;
  const { id: workflowId, workflowType, workflowReference } = parsedWorkflowForSaving;
  const publishedWorkflowId = isPublished ? workflowId : workflowReference?.id;
  const startNode = useSelector(getStartNode);
  const emailListId = startNode?.nodeData?.emailListId;
  const suppressionListIds = startNode?.nodeData?.suppressionListIds;
  const searchQueries = startNode?.nodeData?.searchCombo?.searchQueries;

  const {
    isLoading: currentJourneyIsLoading,
    mutate: currentJourneyMutation,
    data: currentJourneyData,
    error: currentJourneyError,
  } = useRestMutation(JOURNEY_VISITOR_ELIGIBILITY_URL, ApiMethod.post);

  const {
    isLoading: publishedJourneyIsLoading,
    mutate: publishedJourneyMutate,
    data: publishedJourneyData,
    error: currentPublishedError,
  } = useRestMutation(JOURNEY_VISITOR_ELIGIBILITY_URL, ApiMethod.post);

  const checkCurrentJourneyEligibility = useCallback(() => {
    const body = {
      workflowId,
      workflowType,
      nodes: parsedNodes,
      edges: parsedEdges,
      emailListId,
      suppressionListIds,
      searchQueries,
    };
    currentJourneyMutation(body);
  }, [
    workflowId,
    workflowType,
    parsedNodes,
    parsedEdges,
    emailListId,
    suppressionListIds,
    searchQueries,
  ]);

  const checkPublishedJourneyEligibility = useCallback(() => {
    if (!draftOnly) {
      publishedJourneyMutate({
        workflowId: publishedWorkflowId,
        workflowType: WorkflowType.PUBLISHED,
      });
    }
  }, [publishedWorkflowId, draftOnly]);

  return {
    checkCurrentJourneyEligibility,
    checkPublishedJourneyEligibility,
    isLoading: currentJourneyIsLoading || publishedJourneyIsLoading,
    error: currentJourneyError || currentPublishedError,
    currentJourneyEligibility: {
      isJourneyEligible: currentJourneyData?.isEligible || false,
      jvtEligibilityMessage: currentJourneyData?.jvtEligibilityMessage || [],
    },
    publishedJourneyEligibility: {
      isJourneyEligible: publishedJourneyData?.isEligible || false,
      jvtEligibilityMessage: publishedJourneyData?.jvtEligibilityMessage || [],
    },
  };
};
